import {
  DRIFT_PPM,
  EPS_MS,
  MIN_SAMPLES_VALID,
  SAMPLE_MAX_AGE_MS,
  STEP_SILENT_MS,
  WIN_SYNC_MS,
} from '../constants.js';
import type { PingPong, SyncSample } from './types.js';

export type EstimatorEvent =
  | { type: 'CLOCK_STEP'; from: number; to: number; deltaMs: number; rtt: number }
  | { type: 'SYNC_STEP'; from: number; to: number; deltaMs: number; rtt: number }
  | { type: 'SAMPLE_REJECTED'; reason: 'NEGATIVE_RTT' | 'NEGATIVE_HOLD' }
  | { type: 'WINDOW_INVALIDATED'; reason: string };

export interface EstimatorOptions {
  windowMs?: number;
  maxAgeMs?: number;
  minSamples?: number;
  driftPpm?: number;
  epsMs?: number;
}

export interface EstimatorStats {
  count: number;
  rttBest: number | null;
  rttP90: number | null;
  madMs: number;
  sampleAgeMs: number | null;
}

/** The algebra of one exchange (ARCHITECTURE §5.3). */
export function sampleFrom(p: PingPong, wallAtT3: number): SyncSample {
  const rtt = p.t3 - p.t0 - (p.t2 - p.t1);
  const A = (p.t1 - p.t0 + (p.t2 - p.t3)) / 2;
  return { ...p, rtt, A, wallAtT3 };
}

export function median(xs: readonly number[]): number {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 ? (s[mid] as number) : ((s[mid - 1] as number) + (s[mid] as number)) / 2;
}

export function mad(xs: readonly number[]): number {
  if (xs.length === 0) return 0;
  const m = median(xs);
  return median(xs.map((x) => Math.abs(x - m)));
}

/**
 * Min-RTT anchor estimator with a 60 s window and the U_self uncertainty bound (§5.3),
 * anchor adoption / step rules (§5.4). Pure with respect to time: every method that depends on
 * "now" takes the caller's perfNow so it can run under virtual time.
 */
export class OffsetEstimator {
  private readonly windowMs: number;
  private readonly maxAgeMs: number;
  private readonly minSamples: number;
  private readonly driftPpm: number;
  private readonly epsMs: number;

  private samples: SyncSample[] = [];
  private bestInForce: SyncSample | null = null;
  private acceptedSinceInvalidation = 0;
  private wallAnchor: number | null = null;

  constructor(opts: EstimatorOptions = {}) {
    this.windowMs = opts.windowMs ?? WIN_SYNC_MS;
    this.maxAgeMs = opts.maxAgeMs ?? SAMPLE_MAX_AGE_MS;
    this.minSamples = opts.minSamples ?? MIN_SAMPLES_VALID;
    this.driftPpm = opts.driftPpm ?? DRIFT_PPM;
    this.epsMs = opts.epsMs ?? EPS_MS;
  }

  /** Feed one exchange. `wallAtT3` is the client's wall clock read together with t3. */
  addSample(p: PingPong, wallAtT3: number): { accepted: boolean; sample: SyncSample; events: EstimatorEvent[] } {
    const events: EstimatorEvent[] = [];
    const s = sampleFrom(p, wallAtT3);
    if (p.t2 - p.t1 < 0) {
      events.push({ type: 'SAMPLE_REJECTED', reason: 'NEGATIVE_HOLD' });
      return { accepted: false, sample: s, events };
    }
    if (s.rtt < 0) {
      events.push({ type: 'SAMPLE_REJECTED', reason: 'NEGATIVE_RTT' });
      return { accepted: false, sample: s, events };
    }

    this.prune(s.t3);
    const prevBest = this.bestInForce;

    // §5.4: two honest low-RTT samples cannot disagree by more than their half-RTTs plus drift.
    // If they do, a clock step happened: invalidate the window and start over from this sample.
    if (prevBest) {
      const ageOfBest = Math.max(0, s.t3 - prevBest.t3);
      const tolerance = (s.rtt + prevBest.rtt) / 2 + this.driftTerm(ageOfBest) + this.epsMs;
      if (Math.abs(s.A - prevBest.A) > tolerance) {
        events.push({ type: 'SYNC_STEP', from: prevBest.A, to: s.A, deltaMs: s.A - prevBest.A, rtt: s.rtt });
        this.samples = [];
        this.acceptedSinceInvalidation = 0;
        this.bestInForce = null;
      }
    }

    this.samples.push(s);
    this.acceptedSinceInvalidation += 1;

    const newBest = this.minRtt(this.samples);
    if (newBest !== prevBest) {
      if (prevBest && this.bestInForce === prevBest) {
        const delta = newBest.A - prevBest.A;
        if (Math.abs(delta) > STEP_SILENT_MS) {
          events.push({ type: 'CLOCK_STEP', from: prevBest.A, to: newBest.A, deltaMs: delta, rtt: newBest.rtt });
        }
      }
      this.bestInForce = newBest;
    }
    this.wallAnchor = s.t3 + (this.bestInForce as SyncSample).A - wallAtT3;
    return { accepted: true, sample: s, events };
  }

  /** Drop the window (epochId change, suspend detection, SYNC_STEP). The wall anchor survives. */
  invalidate(reason: string): EstimatorEvent {
    this.samples = [];
    this.bestInForce = null;
    this.acceptedSinceInvalidation = 0;
    return { type: 'WINDOW_INVALIDATED', reason };
  }

  /** A in force (serverNow = perfNow + A), or null when nothing has been accepted since invalidation. */
  anchor(): number | null {
    return this.bestInForce?.A ?? null;
  }

  /** A_wall = serverNow − wallNow at the last accepted sample. Survives invalidation (§5.1, §5.5). */
  wallAnchorValue(): number | null {
    return this.wallAnchor;
  }

  /** Install a wall anchor recovered from persistent storage (time_cache, §8.6). */
  restoreWallAnchor(aWall: number): void {
    this.wallAnchor = aWall;
  }

  best(): SyncSample | null {
    return this.bestInForce;
  }

  /** valid ⇔ ≥ 3 accepted samples since invalidation ∧ best sample age ≤ 120 s (§5.3). */
  isValid(nowPerf: number): boolean {
    const b = this.bestInForce;
    if (!b) return false;
    if (this.acceptedSinceInvalidation < this.minSamples) return false;
    return nowPerf - b.t3 <= this.maxAgeMs;
  }

  /** U_self = rtt/2 + drift·age + MAD + EPS (§5.3); null while invalid. */
  uncertainty(nowPerf: number): number | null {
    if (!this.isValid(nowPerf)) return null;
    const b = this.bestInForce as SyncSample;
    this.prune(nowPerf);
    const age = Math.max(0, nowPerf - b.t3);
    return b.rtt / 2 + this.driftTerm(age) + mad(this.samples.map((x) => x.A)) + this.epsMs;
  }

  stats(nowPerf: number): EstimatorStats {
    this.prune(nowPerf);
    const rtts = this.samples.map((x) => x.rtt).sort((a, b) => a - b);
    const b = this.bestInForce;
    return {
      count: this.samples.length,
      rttBest: b ? b.rtt : null,
      rttP90: rtts.length ? (rtts[Math.min(rtts.length - 1, Math.floor(0.9 * rtts.length))] as number) : null,
      madMs: mad(this.samples.map((x) => x.A)),
      sampleAgeMs: b ? Math.max(0, nowPerf - b.t3) : null,
    };
  }

  acceptedCount(): number {
    return this.acceptedSinceInvalidation;
  }

  private driftTerm(ageMs: number): number {
    return this.driftPpm * 1e-6 * ageMs;
  }

  private minRtt(xs: readonly SyncSample[]): SyncSample {
    let best = xs[0] as SyncSample;
    for (const x of xs) if (x.rtt < best.rtt) best = x;
    return best;
  }

  /** Remove samples older than the window. The best in force is retained until it ages out (§5.3). */
  private prune(nowPerf: number): void {
    this.samples = this.samples.filter((x) => nowPerf - x.t3 <= this.windowMs);
    if (this.bestInForce && this.samples.length > 0 && !this.samples.includes(this.bestInForce)) {
      // The window moved past the best: the best remaining sample is the best information we have.
      this.bestInForce = this.minRtt(this.samples);
    }
  }
}
